import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { AxiosError } from 'axios';
import { FormikHelpers } from 'formik/dist/types';
import { useCallback } from 'react';
import { useHistory } from 'react-router';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import { CUSTOM_BACKEND_ERROR_CODES } from '@api/httpClient/backendErrors';
import AccountClientRequests from '@api/requests';

import { useAppDispatch } from '@app/redux/hooks';
import { mfaActions } from '@app/redux/mfa/mfa.reducer';
import RoutePaths from '@app/routes/paths';
import useGenerateRoutePath from '@app/routes/paths/useGenerateRoutePath';

import { LoginFormValues } from '@components/forms/login/interfaces';

import MFA_METHODS_ENUM from '@pages/Verification/MFASetupPage/enums/MFA_METHODS.enum';

type LoginError = {
	messages?: string[];
	errors: {
		code: (typeof CUSTOM_BACKEND_ERROR_CODES)[keyof typeof CUSTOM_BACKEND_ERROR_CODES];
		available_options?: MFA_METHODS_ENUM[];
		description: string;
	}[];
};

function useLogin() {
	const { startRequest } = useTriggeredRequest(AccountClientRequests.Auth.login);
	const generateRoutePath = useGenerateRoutePath();
	const dispatch = useAppDispatch();
	const history = useHistory();

	const onSubmit = useCallback(
		async (values: LoginFormValues, formikHelpers: FormikHelpers<LoginFormValues>) => {
			try {
				const fingerprint = await FingerprintJS.load();
				const { visitorId } = await fingerprint.get();
				await startRequest({ login: values.email, password: values.password, device_id: visitorId });
			} catch (e) {
				const { response } = e as AxiosError<LoginError>;
				const mfaRequiredError = response?.data.errors.find(
					error => error.code === CUSTOM_BACKEND_ERROR_CODES.MFA_REQUIRED_ERR,
				);
				if (mfaRequiredError && mfaRequiredError.available_options) {
					dispatch(
						mfaActions.setMFARequirements({
							mfaPreconditionData: { email: values.email, password: values.password },
							availableMFAMethods: mfaRequiredError.available_options,
						}),
					);
					history.push(generateRoutePath(RoutePaths.VERIFICATION.MFA_VERIFY));
				} else {
					const userBlockedError = response?.data.errors.find(
						error => error.code === CUSTOM_BACKEND_ERROR_CODES.USER_IS_BLOCKED,
					);
					if (userBlockedError) {
						formikHelpers.setErrors({
							loginError:
								'Your account has been blocked. Please contact your administrator for more details or assistance.',
						});
					} else if (
						response?.data.errors.find(
							error => error.code === CUSTOM_BACKEND_ERROR_CODES.INVALID_EMAIL_OR_PASSWORD_OR_TYPE_ERR,
						)
					) {
						formikHelpers.setErrors({
							loginError: 'The credentials provided do not match our records.',
						});
					} else formikHelpers.setErrors({ loginError: 'Oops. Something went wrong.' });
				}
			}
		},
		[startRequest, dispatch, history, generateRoutePath],
	);

	return { initialValues: { email: '', password: '', loginError: '' }, onSubmit };
}

export default useLogin;
